import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MainLayout } from '../layouts';
import { DebugLabel, QuotaExhaustedModal } from '../components/shared';
import { SavedComparisonsLimitModal } from '../components/shared/SavedComparisonsLimitModal';
import { SUPPORTED_MODELS, PROVIDER_BRANDING } from '../config/models';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { useAuth } from '../contexts/AuthContext';
import { useMetaTags } from '../hooks/useMetaTags';
import { UsageTracker } from '../components/homepage/UsageTracker';
import { entitlementService, type EntitlementStatus } from '../services/entitlement';
import { isAdminEmail } from '../utils/adminAccess';

interface Model {
  id: string;
  name: string;
  provider: string;
  selected: boolean;
}

// Add containerClass to provider branding for UI styling
const providerBranding: Record<string, { logoSrc: string; alt: string; containerClass?: string }> = Object.entries(PROVIDER_BRANDING).reduce((acc, [key, val]) => ({
  ...acc,
  [key]: { ...val, containerClass: 'bg-white/90' }
}), {});

interface LocationState {
  prompt: string;
  selectedModels: Model[];
}

interface UsageState {
  used: number;
  total: number;
  canSubmit: boolean;
  buttonText: string;
  progressColor: string;
  planCode?: string;
  upgradeUrl?: string;
}

export const ComparisonSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;

  const { user, dbProfile } = useAuth();
  useMetaTags({
    title: 'Run a Comparison | Smart Prompt',
    url: 'https://www.smartprompt.us/compare'
  });

  const paywallEnabled = useFeatureFlag('PAYWALL');
  // NEW_DESIGN_BETA graduated - features always enabled

  const [prompt, setPrompt] = useState(state?.prompt || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageState, setUsageState] = useState<UsageState | null>(null);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showSavedLimitModal, setShowSavedLimitModal] = useState(false);
  const [entitlementStatus, setEntitlementStatus] = useState<EntitlementStatus | null>(null);
  const [models, setModels] = useState<Model[]>(
    state?.selectedModels?.length
      ? SUPPORTED_MODELS.map(m => ({
          id: m.id,
          name: m.name,
          provider: m.provider,
          selected: state.selectedModels.some(sm => sm.id === m.id)
        }))
      : SUPPORTED_MODELS.map(m => ({
          id: m.id,
          name: m.name,
          provider: m.provider,
          selected: false
        }))
  );

  // Textarea ref for auto-resize on mobile
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Read from sessionStorage if available (from Edit & Resubmit flow)
  useEffect(() => {
    const editPrompt = sessionStorage.getItem('editPrompt');
    const editModels = sessionStorage.getItem('editModels');

    if (editPrompt) {
      setPrompt(editPrompt);
      // Clear after reading so it doesn't persist across sessions
      sessionStorage.removeItem('editPrompt');
      sessionStorage.removeItem('editTitle');
    }

    if (editModels) {
      try {
        const modelIds = JSON.parse(editModels);
        setModels(prev =>
          prev.map(model => ({
            ...model,
            selected: modelIds.includes(model.id)
          }))
        );
        sessionStorage.removeItem('editModels');
      } catch (err) {
        console.error('Failed to parse edit models:', err);
      }
    }
  }, []); // Empty dependency array = run once on mount

  // Fetch entitlements on mount
  useEffect(() => {
    // If paywall is disabled, use unlimited defaults and skip fetching
    if (!paywallEnabled) {
      const unlimitedDefaults = entitlementService.getUnlimitedDefaults();
      setEntitlementStatus(unlimitedDefaults);
      console.log('[ComparisonSetup] Paywall disabled - using unlimited defaults');
      return;
    }

    // Check cache first (synchronous) to avoid flash
    const cachedStatus = entitlementService.getCachedStatusSync();
    if (cachedStatus) {
      setEntitlementStatus(cachedStatus);
      console.log('[ComparisonSetup] Using cached entitlements:', cachedStatus.plan?.displayName || 'unknown plan');
    }

    const fetchEntitlements = async () => {
      try {
        const status = await entitlementService.getStatus();

        // If paywall is disabled in response, use unlimited defaults
        if (!status.paywallEnabled) {
          const unlimitedDefaults = entitlementService.getUnlimitedDefaults();
          setEntitlementStatus(unlimitedDefaults);
          console.log('[ComparisonSetup] Paywall disabled in response - using unlimited defaults');
          return;
        }

        setEntitlementStatus(status);
      } catch (err) {
        console.error('[ComparisonSetup] Failed to fetch entitlements:', err);
        // Fail-open: use unlimited defaults on error
        const unlimitedDefaults = entitlementService.getUnlimitedDefaults();
        setEntitlementStatus(unlimitedDefaults);
        console.log('[ComparisonSetup] Using unlimited defaults due to error');
      }
    };
    fetchEntitlements();
  }, [paywallEnabled]);

  // Check quota and show modal immediately if exhausted
  useEffect(() => {
    if (usageState && !usageState.canSubmit) {
      setShowQuotaModal(true);
    }
  }, [usageState]);

  // Check saved comparisons limit when entitlements are loaded
  useEffect(() => {
    if (!paywallEnabled || !entitlementStatus || !user) return;

    const savedQuota = entitlementStatus.quotas?.savedComparisons;
    if (savedQuota && !savedQuota.allowed) {
      setShowSavedLimitModal(true);
    }
  }, [entitlementStatus, paywallEnabled, user]);

  // Auto-resize textarea on mobile when content changes
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get proper scrollHeight
    textarea.style.height = 'auto';
    // Set height to scrollHeight to fit content
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [prompt]);

  const toggleModel = (modelId: string) => {
    setModels(prev => {
      const updatedModels = prev.map(model =>
        model.id === modelId ? { ...model, selected: !model.selected } : model
      );

      // Check if toggling ON would exceed maxModelsPerComparison
      const model = prev.find(m => m.id === modelId);
      if (model && !model.selected) {
        const selectedCount = updatedModels.filter(m => m.selected).length;
        const maxModels = entitlementStatus?.plan?.maxModelsPerComparison || 10;

        if (selectedCount > maxModels) {
          setError(`You can select up to ${maxModels} models on your ${entitlementStatus?.plan?.displayName || 'current'} plan. ${entitlementStatus?.plan?.code === 'anonymous' ? 'Sign in' : 'Upgrade'} for more.`);
          return prev; // Don't toggle
        }
      }

      return updatedModels;
    });
  };

  const allSelected = models.every(model => model.selected);

  const handleToggleAll = () => {
    const maxModels = entitlementStatus?.plan?.maxModelsPerComparison || 10;

    if (!allSelected && models.length > maxModels) {
      setError(`You can select up to ${maxModels} models on your ${entitlementStatus?.plan?.displayName || 'current'} plan. ${entitlementStatus?.plan?.code === 'anonymous' ? 'Sign in' : 'Upgrade'} for more.`);
      return;
    }

    setModels(prev => prev.map(model => ({ ...model, selected: !allSelected })));
  };

  const selectedCount = models.filter(m => m.selected).length;

  const handleViewResults = () => {
    navigate('/results');
  };

  // Mobile-only submit button with up arrow icon
  const renderMobileSubmitButton = () => (
    <button
      disabled={!prompt.trim() || selectedCount < 1 || loading || (usageState && !usageState.canSubmit)}
      onClick={handleCompareClick}
      className="absolute bottom-3 right-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white p-3 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-cyan-500 disabled:hover:to-blue-500 flex items-center justify-center"
      aria-label="Submit prompt"
    >
      {loading ? (
        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      )}
    </button>
  );

  const renderCompareButtonContent = () => {
    if (loading) {
      return (
        <span className="flex items-center justify-center space-x-2">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Creating Comparison...</span>
        </span>
      );
    }

    if (usageState?.buttonText) {
      return usageState.buttonText;
    }

    return 'Submit Prompt';
  };

  const renderCompareButton = (additionalClasses?: string) => (
    <button
      disabled={!prompt.trim() || selectedCount < 1 || loading || (usageState && !usageState.canSubmit)}
      onClick={handleCompareClick}
      className={`w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-12 py-4 rounded-lg font-semibold text-lg hover:from-cyan-600 hover:to-blue-600 hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${additionalClasses || ''}`}
    >
      {renderCompareButtonContent()}
    </button>
  );

  const handleCompareClick = async () => {
    if (prompt.trim() && selectedCount >= 1) {
      try {
        if (!user) {
          setError('Please sign in to create a comparison.');
          return;
        }

        setLoading(true);
        setError(null);

        const selectedModelIds = models
          .filter(m => m.selected)
          .map(m => m.id);

        console.log('[ComparisonSetup] Creating comparison via API:', {
          prompt: prompt.substring(0, 50) + '...',
          models: selectedModelIds
        });

        // Call backend API to create comparison
        const idToken = await user.getIdToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        if (idToken) {
          headers['Authorization'] = `Bearer ${idToken}`;
        }

        const response = await fetch('/api/comparison/create', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            prompt: prompt,
            models: selectedModelIds
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('[ComparisonSetup] Comparison created:', data);

        // Navigate to processing screen with comparison ID (or slug if available)
        const idOrSlug = data.slug || data.comparisonId;
        navigate(`/compare/${idOrSlug}/processing`, {
          state: {
            comparisonId: data.comparisonId,
            slug: data.slug,
            prompt,
            models: selectedModelIds
          }
        });

      } catch (err) {
        console.error('[ComparisonSetup] Failed to create comparison:', err);
        setError(err instanceof Error ? err.message : 'Failed to create comparison');
        setLoading(false);
      }
    }
  };

  return (
    <MainLayout>
      <DebugLabel label="ComparisonSetupPage-Container" type="page" position="top-left" />

      {/* Main Content */}
      <div className={`max-w-6xl mx-auto px-6 py-8 relative ${
        usageState && !usageState.canSubmit ? 'blur-sm opacity-60 pointer-events-none' : ''
      }`}>
        <DebugLabel label="ComparisonSetup-MainContent" type="section" position="bottom-left" />

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-400/50 rounded-lg">
            <div className="flex items-start space-x-3">
              <span className="text-red-400 text-xl">⚠️</span>
              <div className="flex-1">
                <h3 className="text-red-300 font-semibold mb-1">
                  {error.includes('select up to') ? 'Model limit reached' : 'Comparison Failed'}
                </h3>
                <p className="text-red-200 text-sm">
                  {error.includes('select up to') ? (
                    <>
                      Your current plan allows {entitlementStatus?.plan?.maxModelsPerComparison || 2} models. Please{' '}
                      <a
                        href="https://www.smartprompt.us/billing/upgrade"
                        className="underline hover:text-red-100"
                      >
                        upgrade
                      </a>
                      {' '}to increase the limit.
                    </>
                  ) : (
                    error
                  )}
                </p>
                <button
                  onClick={() => setError(null)}
                  className="mt-2 text-red-300 text-sm underline hover:text-red-200"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-lg overflow-hidden md:grid md:grid-cols-2 md:gap-8 md:items-stretch md:bg-transparent md:border-0 md:backdrop-blur-0 md:rounded-none md:overflow-visible">
          {/* Left Panel (Mobile: Top) - Model Selection */}
          <div className="p-6 md:p-0 md:order-2 md:h-full">
            <div className="bg-transparent md:bg-slate-800/30 md:backdrop-blur-sm md:border md:border-slate-700/50 rounded-none md:rounded-lg p-0 md:p-6 relative md:h-full md:flex md:flex-col">
              <DebugLabel label="ComparisonSetup-ModelSelection" type="component" position="bottom-right" />
              <div className="flex items-center space-x-2 mb-6">
                {/* Mobile: Step 1, Desktop: Step 2 */}
                <div className="w-6 h-6 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center md:hidden">
                  <span className="text-white text-xs font-bold">1</span>
                </div>
                <div className="w-6 h-6 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full items-center justify-center hidden md:flex">
                  <span className="text-white text-xs font-bold">2</span>
                </div>
                {(() => {
                  const maxModelsAllowed = entitlementStatus?.plan?.maxModelsPerComparison || 10;
                  const visibleModels = models.length;
                  const displayCap = Math.min(maxModelsAllowed, visibleModels);

                  return (
                    <h2 className="text-base md:text-lg font-semibold text-white">
                      AI Models ({selectedCount} of {displayCap})
                    </h2>
                  );
                })()}
              </div>

            {/* Desktop: Two-column grid with slim rectangle buttons */}
            <div className="hidden md:grid md:grid-cols-2 md:gap-3">
              {models.map((model) => {
                const branding = providerBranding[model.provider];
                const fallbackBadgeClass =
                  model.provider === 'OpenAI'
                    ? 'bg-gray-600'
                    : model.provider === 'Anthropic'
                    ? 'bg-orange-500'
                    : model.provider === 'Google'
                    ? 'bg-blue-500'
                    : model.provider === 'xAI'
                    ? 'bg-yellow-500'
                    : model.provider === 'DeepSeek'
                    ? 'bg-gray-700'
                    : 'bg-blue-600';

                return (
                  <button
                    key={model.id}
                    onClick={() => toggleModel(model.id)}
                    className={`w-full flex items-center gap-3 p-3 border-2 rounded-lg transition-all hover:scale-[1.02] active:scale-95 ${
                      model.selected
                        ? 'border-cyan-400 bg-cyan-500/20'
                        : 'border-slate-600 bg-slate-900/30 hover:border-slate-500'
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        branding
                          ? `${branding.containerClass ?? 'bg-white/90'} ring-1 ring-slate-700/40 p-1`
                          : fallbackBadgeClass
                      }`}
                    >
                      {branding ? (
                        <img
                          src={branding.logoSrc}
                          alt={branding.alt}
                          className="w-8 h-8 object-contain rounded-md"
                        />
                      ) : (
                        <span className="text-white font-semibold text-sm">
                          {model.name.substring(0, 1)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-medium text-white text-left flex-1">
                      {model.name}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Mobile: Two-column grid with horizontal tap-to-select buttons */}
            <div className="md:hidden grid grid-cols-2 gap-2">
              {models.map((model) => {
                const branding = providerBranding[model.provider];
                const fallbackBadgeClass =
                  model.provider === 'OpenAI'
                    ? 'bg-gray-600'
                    : model.provider === 'Anthropic'
                    ? 'bg-orange-500'
                    : model.provider === 'Google'
                    ? 'bg-blue-500'
                    : model.provider === 'xAI'
                    ? 'bg-yellow-500'
                    : model.provider === 'DeepSeek'
                    ? 'bg-gray-700'
                    : 'bg-blue-600';

                return (
                  <button
                    key={model.id}
                    onClick={() => toggleModel(model.id)}
                    className={`w-full flex items-center gap-3 p-3 border-2 rounded-lg transition-all active:scale-95 ${
                      model.selected
                        ? 'border-cyan-400 bg-cyan-500/20'
                        : 'border-slate-600 bg-slate-900/30'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        branding
                          ? `${branding.containerClass ?? 'bg-white/90'} ring-1 ring-slate-700/40 p-1`
                          : fallbackBadgeClass
                      }`}
                    >
                      {branding ? (
                        <img
                          src={branding.logoSrc}
                          alt={branding.alt}
                          className="w-6 h-6 object-contain rounded-md"
                        />
                      ) : (
                        <span className="text-white font-semibold text-xs">
                          {model.name.substring(0, 1)}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-medium text-white text-left flex-1">
                      {model.name}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Only show Select all for Nexus users and admins */}
            {(entitlementStatus?.plan?.code === 'nexus' || isAdminEmail(user?.email)) && (
              <div className="mt-4 text-right">
                <button
                  type="button"
                  onClick={handleToggleAll}
                  className="px-3 py-1.5 text-xs font-medium bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 hover:text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/50 rounded-lg transition-all"
                >
                  {allSelected ? 'Deselect all models' : 'Select all models'}
                </button>
              </div>
            )}
            {/* Desktop Compare CTA + Usage Tracker under models */}
            <div className="hidden md:flex md:flex-col md:gap-4 md:mt-6">
              <div className="w-full flex justify-center">
                {renderCompareButton('md:w-auto md:min-w-[220px] md:px-10 md:py-3')}
              </div>
              {paywallEnabled && (
                <div className="w-full">
                  <UsageTracker onUsageChange={setUsageState} />
                </div>
              )}
            </div>
            </div>
          </div>

          {/* Right Panel (Mobile: Bottom) - Prompt Input */}
          <div className="p-6 md:p-0 border-t border-slate-700/60 md:border-0 md:order-1 md:h-full">
            <div className="space-y-6 bg-transparent md:bg-slate-800/30 md:backdrop-blur-sm md:border md:border-slate-700/50 rounded-none md:rounded-lg p-0 md:p-6 relative md:flex md:flex-col md:h-full md:space-y-0 md:gap-6">
              <DebugLabel label="ComparisonSetup-PromptInput" type="component" position="top-left" />
              <div className="flex items-center space-x-2 mb-4">
                {/* Mobile: Step 2, Desktop: Step 1 */}
                <div className="w-6 h-6 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center md:hidden">
                  <span className="text-white text-xs font-bold">2</span>
                </div>
                <div className="w-6 h-6 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full items-center justify-center hidden md:flex">
                  <span className="text-white text-xs font-bold">1</span>
                </div>
                <h2 className="text-lg font-semibold text-white">Your Prompt</h2>
              </div>

              <div className="space-y-6 md:flex md:flex-col md:gap-4 md:flex-1">
                {/* Mobile: Textarea with embedded submit button */}
                <div className="relative md:flex md:flex-1">
                  <textarea
                    ref={textareaRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onFocus={() => {
                      // Auto-scroll to bring textarea and button near keyboard on mobile
                      setTimeout(() => {
                        textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                      }, 100);
                    }}
                    placeholder="Enter your prompt here..."
                    rows={1}
                    className="w-full p-4 pr-14 bg-slate-900/50 border border-slate-600/50 rounded-lg resize-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 text-white placeholder-slate-400 transition-colors overflow-hidden md:flex-1 md:min-h-full md:pr-4 md:resize-none"
                  />
                  {/* Mobile-only submit button overlayed inside textarea */}
                  <div className="md:hidden">
                    {renderMobileSubmitButton()}
                  </div>
                </div>

                {/* Compare CTA and Usage Tracker (Desktop) */}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Quota Exhausted Modal */}
      <QuotaExhaustedModal
        isOpen={showQuotaModal}
        usedCount={usageState?.used || 0}
        totalCount={usageState?.total || 0}
        planName={usageState?.planCode || 'Free'}
        blocking={true}
        userTimezone={entitlementStatus?.userTimezone || dbProfile?.timezone || 'UTC'}
      />

      {/* Saved Comparisons Limit Modal - Blocking when limit reached */}
      {showSavedLimitModal && entitlementStatus?.quotas?.savedComparisons && (
        <SavedComparisonsLimitModal
          quota={entitlementStatus.quotas.savedComparisons}
          planName={entitlementStatus.plan.displayName}
          planCode={entitlementStatus.plan.code}
          onViewResults={handleViewResults}
        />
      )}
    </MainLayout>
  );
};
